declare let window: any

import _ from 'lodash'
import queryString from 'query-string'
import React from 'react'

import { EntityRenderer } from 'shared-libs/components/entity/renderer'
import { EdgeDetails, Entity } from 'shared-libs/models/entity'
import { Store } from 'shared-libs/models/store'
import { getErrorMessage, getErrorStack } from 'shared-libs/helpers/errors'

import apis, { PubSubMessage } from 'browser/app/models/apis'
import ComponentsMap from 'browser/components'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import {
  IFormFocusHandle,
  FormFocusHandleContext,
  FormFocusHandleSetter,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { FormFocusTargetProvider } from 'browser/components/ui2/behavior/form-focus-target-provider'
import { EntityErrorSummaryBanner } from 'browser/components/atomic-elements/atoms/error-block/entity-error-summary-banner'
import { EntityFormValidationContext } from 'browser/components/atomic-elements/organisms/entity/entity-form-validation-context'
import { IContainerProps } from 'browser/components/containers/container-props'
import { EntitySubscriptionManager } from 'browser/app/utils/entity-subscription-manager'
import { stringToBoolean } from 'browser/app/utils/utils'

export interface IAbstractEntityViewState {
  entity: Entity
  errors: any
  hasSubmitted: boolean
  isLoading: boolean
  isSaving: boolean
  uiErrors?: Record<string, string[]>
  uiErrorLabels?: Record<string, string>
  settings?: any
}

export abstract class AbstractEntityView<S extends IAbstractEntityViewState>
  extends React.Component<IContainerProps, S> {

  protected store: Store
  private edges: string[] = []
  protected focusHandleRef: { current: IFormFocusHandle | null } = { current: null }
  protected setFocusHandle: FormFocusHandleSetter = (h) => { this.focusHandleRef.current = h }
  private currentUIErrors: Record<string, string[]> = {}

  constructor(props) {
    super(props)
    this.store = apis.getStore()
    window.addEventListener('beforeunload', this.componentWillUnmount.bind(this))
  }

  public componentDidMount() {
    const entityId = this.getEntityIdFromProps(this.props)
    this.fetchModel(entityId)
      .then(() => {
        this.subscribeToEntityUpdates()
      })
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    const entityId = this.getEntityIdFromProps(this.props)
    const nextEntityId = this.getEntityIdFromProps(nextProps)
    if (entityId !== nextEntityId) {
      this.fetchModel(nextEntityId)
    }
  }

  public componentWillUnmount(): void {
    const updatesEnabled = stringToBoolean(apis.getSettings().getConfigValue('entityUpdates'))
    if (updatesEnabled) {
      EntitySubscriptionManager.removeEntityUpdateSubscriptions(this.edges)
      EntitySubscriptionManager.removeEntityUpdateListener(this.handlePubsubMessage)
    }
  }

  public render() {
    const { viewSchema } = this.props
    const { entity, errors, hasSubmitted, isLoading, uiErrors, uiErrorLabels } = this.state
    if (isLoading) {
      return this.renderLoading()
    }
    const entityRendererContext = {
      density: 'collapse',
      isHorizontalLayout: true,
      onSave: this.handleSave,
    }
    return (
      <EntityFormValidationContext.Provider value={{ entity, errors, hasSubmitted, uiErrors, uiErrorLabels }}>
      <FormFocusHandleContext.Provider value={this.setFocusHandle}>
        <FormFocusTargetProvider>
          <div
            className='grid-block c-appBody'
          >
            <EntityErrorSummaryBanner
              uiErrors={uiErrors}
              uiErrorLabels={uiErrorLabels}
              errors={errors}
              entity={entity}
              isVisible={hasSubmitted}
            />
            <EntityRenderer
              actions={this}
              componentsMap={ComponentsMap}
              context={entityRendererContext}
              errors={errors}
              hasSubmitted={hasSubmitted}
              onChangeComplete={this.handleEntityChange}
              onValidate={this.handleValidate}
              state={this.state}
              uiContext={viewSchema}
              uiSchemaPath={[]}
              value={entity}
            >
              {this.props.children}
            </EntityRenderer>
          </div>
        </FormFocusTargetProvider>
      </FormFocusHandleContext.Provider>
      </EntityFormValidationContext.Provider>
    )
  }

  protected renderLoading() {
    return (
      <div className='grid-block c-appBody'>
        <LoadingSpinner />
      </div>
    )
  }

  protected fetchModel(entityId) {
    this.setState({ isLoading: true })
    return this.store.findRecord(entityId).then((entity) => {
      this.setState({ entity, isLoading: false })
    }).catch((error) => { throw error })
  }

  protected getEntityIdFromProps(props) {
    const { location } = props
    const { entityId } = queryString.parse(location.search)
    return entityId
  }

  protected handleEntityChange = (entity) => {
    this.forceUpdate()
  }

  protected handleOpenModal = ({ overlayName, overlayProps }) => {
    const ComponentClass = (ComponentsMap as any)[overlayName]
    ComponentClass.open(overlayProps)
  }

  protected handleValidate = (
    uiErrors: Record<string, string[]>,
    uiErrorLabels: Record<string, string> = {},
  ) => {
    this.currentUIErrors = uiErrors
    if (
      !_.isEqual(this.state.uiErrors, uiErrors) ||
      !_.isEqual(this.state.uiErrorLabels, uiErrorLabels)
    ) {
      this.setState({ uiErrors, uiErrorLabels } as Partial<S> as any)
    }
  }

  protected handleRollback = () => {
    const { entity } = this.state
    entity.rollback()
    this.setState({ hasSubmitted: false })
    this.forceUpdate()
  }

  protected handleSave = async () => {
    const { entity } = this.state
    // Pre-save gate: block the network call if the renderer reports any
    // unfilled required fields, and surface them by setting hasSubmitted.
    if (!_.isEmpty(this.currentUIErrors)) {
      this.setState({ hasSubmitted: true })
      void this.focusHandleRef.current?.focusFirstError()
      return
    }
    this.setState({ hasSubmitted: true, isSaving: true })
    const errors = await entity.validate()
    if (_.isEmpty(errors)) {
      entity.save()
        .then(() => this.setState({ errors: null, hasSubmitted: false, isSaving: false }))
        .catch((saveErrors: any) => {
          this.setState({ errors: saveErrors?.errors ?? saveErrors, isSaving: false })
          void this.focusHandleRef.current?.focusFirstError()
        })
    } else {
      this.setState({ errors, isSaving: false })
      void this.focusHandleRef.current?.focusFirstError()
    }
  }

  private async subscribeToEntityUpdates() {
    const updatesEnabled = stringToBoolean(apis.getSettings().getConfigValue('entityUpdates'))
    if (!updatesEnabled) return
    const { entity } = this.state
    if (!entity) return
    const edges = _.map(entity.getEdges?.(), (edge: EdgeDetails) => edge.value?.entityId)
    try {
      await EntitySubscriptionManager.diffEntitySubscriptions(this.edges, edges, this.handlePubsubMessage)
    } catch(err) {
      console.log(`failed to update subscriptions: ${getErrorStack(err)}`)
    }
    this.edges = edges
  }

  private handlePubsubMessage = async (e: PubSubMessage) => {
    const uri = _.get(e, 'data')
    if (!uri) {
      return
    }
    try {
      const parsedUri = new URL(uri)
      if (parsedUri.pathname !== '/actions/entity/update') return
      // Mobile and web seem to present the notification differently
      const json = parsedUri.searchParams.get('jsonProps').replace(/\\/g,'')
      const payload = JSON.parse(json)
      const { entity } = this.state
      await Promise.all([entity.reload(), ..._.map(payload.updatedEntities, async (updated) => {
        if (entity.uniqueId === updated) {
          return Promise.resolve()
        }
        const updatedEntity = apis.getStore().getRecord(updated)
        if (_.isNil(updatedEntity)) {
          return apis.getStore().getOrFetchRecord(updated)
        } else {
          return updatedEntity.reload()
        }
      })])
      this.forceUpdate()
    } catch (err) {
      console.warn(`failed to parse notification: ${getErrorMessage(err)}\n${getErrorStack(err)}`)
    }
  }
}
